import { AlertColor } from "@mui/material";
import { industries, workDescription } from "components/user/sign-in/states";

type Industry = (typeof industries)[number];

type WorkDescription = (typeof workDescription)[number];

interface CreateRelease {
  name: string;
  year: number;
  month: number;
  products: string[];
}

interface Organisation {
  name: string;
  email: string;
  address: string;
  abn?: string;
  phoneNumber?: string;
  id?: string;
  industry?: string;
  addressId?: string;
  website?: string;
  orgSize?: string;
  hasUsedTrial: boolean;
}

interface PaymentMethod {
  last4: string;
  expireMonth: number;
  expireYear: number;
  active: boolean;
  method: string;
  expired: boolean;
}

interface PaymentMethodAdmin extends PaymentMethod {
  displayString: string;
}

type BillingInterval = "day" | "month" | "year";

interface PortalPlan {
  productId: string;
  priceId: string;
  name: string;
  price: number;
  interval: BillingInterval;
  overageRate: number;
  creditAmount: number;
  mailpointAccess: boolean;
  rateLimit: number;
  isEnterprise: boolean;
}

type HubPlan = {
  display_name: string;
  price_lookup_id: string;
  product_display_name: string;
  cadence: BillingInterval;
  dollar_cost: number;
  credit_amount: number;
  rate_limit: number;
  externally_managed: boolean;
  product_key: string;
  overage_cost_dollars: number | null;
  overage_unlimited: boolean;
  tier: number;
  deprecated?: boolean;
  custom_rate_limit?: boolean;
  custom_credit_limit?: boolean;
  free_plan: boolean;
  trial_allowed: boolean;
  legacy: boolean;
};

interface PortalSubscriptionSchedule {
  id: string;
  startTime: number;
  plan: HubPlan;
}

type TrialType = "admin" | "user" | null;

interface PortalSubscription {
  renewalDate: number;
  currentPeriodStart: number;
  subscriptionId: string;
  enterpriseCreditAmountOverride: number;
  nextSub?: PortalSubscriptionSchedule;
  cancelAtPeriodEnd: boolean;
  plan: HubPlan;
  isMyob: boolean;
  trialType: TrialType;
}

type Duration = "forever" | "once" | "repeating";

interface AppliesTo {
  products: string[];
  price_keys: string[];
}

interface Coupon {
  id: string;
  percent_off?: number;
  amount_off?: number;
  duration: Duration;
  valid: boolean;
  name: string;
  applies_to?: AppliesTo;
}

interface Subscription {
  activeSubscription?: PortalSubscription;
  availablePlans: HubPlan[];
  overageLimitDollars?: number;
}

interface AdminSubscription extends Subscription {
  rateLimit?: number;
}

type ProductFeatureChip = {
  colour:
    | "success"
    | "info"
    | "warning"
    | "error"
    | "default"
    | "primary"
    | "secondary";
  text: string;
};

type ProductIcon = "info" | "check" | "plus";

type ProductFeature = {
  icon: ProductIcon;
  text: string;
  chip?: ProductFeatureChip | null;
  cadence?: BillingInterval;
};

type FeatureBreakdown = {
  category: string;
  key: string;
  icon?: ProductIcon;
  value?: string;
  description?: string;
};

type Product = {
  displayName: string;
  productKey: string;
  selfServe: boolean;
  features: ProductFeature[];
  featureBreakdown: FeatureBreakdown[];
};

interface PlanInfo {
  availablePlans: HubPlan[];
  products: Product[];
  creditUse: CreditUse[];
}
interface CreditItem {
  premiumData?: ProductIcon;
  method?: string;
  credits: string;
  description?: string;
}
interface CreditUse {
  category: string;
  product: string;
  items: CreditItem[];
}

interface UpdateSubscription {
  priceLookupId: string;
  overageLimitDollars: number;
  coupon?: string;
  trial: boolean;
}

interface FutureSubscription {
  id: string;
  plan: HubPlan;
  startTime: number;
}

interface Overage {
  enabled: boolean;
  dollarsSpent: number;
  dollarsLimit: number;
  creditsSpent: number;
  creditsLimit: number;
  overageRate: number; // Dollars per credit
}

interface DatasetList {
  datasets: Dataset[];
  links?: {
    numberMatched: number;
    numberReturned: number;
    self: string;
    next?: string;
  };
  messages: string[];
  atribution: string;
}

interface CustomDatasetList extends Omit<DatasetList, "datasets"> {
  datasets: CustomDataset[];
}

interface RejectedInvite {
  organisationId: string;
  userMemberId: string;
}

enum Role {
  OWNER = "owner",
  ADMIN = "admin",
  VIEWER = "viewer",
  ANALYST = "analyst",
  DEVELOPER = "developer",
  BILLING = "billing",
}

// The data as it is in dynamo
interface OrgMembership {
  organisationId: string;
  userMemberId: string;
  status: string;
  role?: Role;
  invitedOn?: number;
  acceptedOn?: number;
}

// Org membership details with a bunch of extra junk on them
interface UserMembership extends OrgMembership {
  organisationName: string;
  organisationEmail: string;
  userFirstName: string;
  userLastName: string;
  userFullName: string;
  userEmail: string;
  subscription?: string;
  hasAccess?: boolean; // Whether user can access this org (owner or members.create allowed)
}

interface BillingUsage {
  creditsUsedTotal: number;
  overageDollars: number;
  overageCredits: number;
  allowance: number;
}

type Status = `approved` | `pending` | `revoked` | "approve" | "revoke";
type DeveloperStatus = "active" | "inactive";

interface ApigeeDeveloperStatus {
  apigeeDeveloperId: string;
  action: DeveloperStatus;
}

interface UserStatus {
  status: DeveloperStatus;
}

interface CustomDatasetFilterType {
  name: string[];
  query: string;
  product: string[];
  release?: string;
  latestRelease: boolean;
  customerName: string[];
  notPublished: boolean;
}
interface DatasetFiltersType {
  query: string;
  product: string[];
  region: string[];
  format: string[];
  datum: string[];
  release?: string;
  latestRelease: boolean;
  licenced: boolean;
  notPublished?: boolean;
}

interface BaseApiKey {
  name: string;
  status: string;
  notes: string;
  displayName: string;
}

interface ModifyApiKey extends BaseApiKey {
  products: string[];
}

interface ApiKey extends BaseApiKey {
  createdDate: number;
  expireDate: number;
  consumerKey: string;
  consumerSecret: string;
  products: {
    productName: string;
    productDisplayName: string;
  }[];
}

interface ApiProduct {
  name: string;
  displayName: string;
  premium?: "full" | "partial";
}

interface AlertState {
  apigeeDeveloperId: string;
  developerEmail: string;
  developerName: string;
  message: string;
  severity: AlertColor;
}

interface AdminOrganisationInfo {
  name: string;
  email: string;
  abn: string;
  address: string;
  addressId: string;
  phoneNumber: string;
  industry: string;
  website: string;
  orgSize: string;
  id: string;
  apigeeDeveloperId: string;
  stripeAccountId: string;
  salesforceAccountId: string;
  accountCreationDate?: number;
  status: DeveloperStatus;
}

interface AdminSearchOrganisation {
  name: string;
  email: string;
  isOrganisation: boolean;
  customerId: string;
  createdInStripe: number;
  subscription: string;
  apigeeDeveloperId: string;
  organisationId: string;
}

type CognitoStatus =
  | "UNCONFIRMED"
  | "CONFIRMED"
  | "ARCHIVED"
  | "COMPROMISED"
  | "UNKNOWN"
  | "RESET_REQUIRED"
  | "FORCE_CHANGE_PASSWORD"
  | "EXTERNAL_PROVIDER";

interface AdminSearchAccount {
  username: string;
  status: CognitoStatus;
  enabled: boolean;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  terms: boolean;
  sub: string;
  created: number;
}

interface MemberAccountDetails {
  username: string;

  status: CognitoStatus;
  email: string;
  firstName: string;
  lastName: string;
  created: number;
}

interface AdminAccountDetails extends MemberAccountDetails {
  enabled: boolean;
  emailVerified: boolean;
  terms: boolean;
  sub: string;
  phone: string;
  address: string;
  cognitoMfaEnabled: boolean;
}

interface Dataset {
  name: string;
  description: string;
  product: string;
  region: string;
  format: string;
  release: string;
  datum: string;
  publishDate: string;
  version: string;
  classification: string;
  id: string;
  size: string;
  status: string;
  licenced: boolean;
}

interface CustomDataset extends Dataset {
  creator: string;
  apigeeDeveloperId: string;
  customerName: string;
  docsLink?: string;
  fileName?: string;
  expiry?: number; // epoch milliseconds
}

interface OrganisationInvite {
  organisationId: string;
  userEmail: string;
  invitationStatus: string;
}

interface CustomDatasetDefinitions {
  names: string[];
  regions: string[];
  formats: string[];
  datums: string[];
  releases: string[];
  products: string[];
}

interface DefinitionsMetadata {
  regions: string[];
  formats: string[];
  datums: string[];
  latestRelease: string;
}

interface ProductDefinition {
  value: string;
  displayName: string;
}

interface DatasetDefinitions {
  products: ProductDefinition[];
  regions: string[];
  formats: string[];
  datums: string[];
  metadata: { [key: string]: DefinitionsMetadata };
  releaseGroups: { [key: string]: string };
}

interface CustomDatasetDefinitions {
  names: string[];
  regions: string[];
  formats: string[];
  datums: string[];
  releases: string[];
}

interface Release {
  key: string;
  publish_date: number;
  name: string;
  year: number;
  month: number;
  products: string[];
  staged: ReleaseEventSummary[];
  loaded: ReleaseEventSummary[];
  published: ReleaseEventSummary[];
  creator?: string;
  status: ReleaseStatus;
}

type ReleaseStatus = "internal" | "published" | "staged" | "draft";
type DatasetStatus = "internal" | "published" | "loading";

interface ReleaseEventSummary {
  product: string;
  size: number;
  success_count: number;
  failed_count: number;
  success: string[];
  failed: FailedReleaseEvent[];
}

interface FailedReleaseEvent {
  id: string;
  error: string;
}

interface Dictionary<T> {
  [Key: string]: T;
}

interface CustomDatasetsPost {
  name: string;
  s3Path: string;
  description?: string;
  region: string;
  format: string;
  release: string;
  datum: string;
  version: string;
  apigeeDeveloperId: string;
  creator: string;
  publish?: boolean;
  fileName: string;
  docsLink?: string;
  expiry?: number; // epoch milliseconds
}

interface CustomDatasetEdit {
  name: string;
  region: string;
  format: string;
  release: string;
  datum: string;
  version: string;
  apigeeDeveloperId: string;
  expiry?: number; // epoch milliseconds
  docsLink?: string;
}

type ApiProducts =
  | "Administrative Boundaries API"
  | "Land Parcels API"
  | "Datasets Internal"
  | "Batches API"
  | "Predictive AV"
  | "Buildings API"
  | "Addresses API";

type DataType =
  | "Point"
  | "Polygon"
  | "Multi Polygon"
  | "Line"
  | "Multi Line"
  | "Rastor"
  | "Table";
type UpdateCycles = "Daily" | "Monthly" | "Quarterly" | "Yearly";
type LicenseTypes = "Commercial" | "Creative Commons";

interface Metadata {
  dateAdded: number;
  dataType: string[];
  totalFeatureCount: number;
  updateFrequency: string;
  license: string;
  licenseLink?: string;

  metadataXml?: string;
  metadataJson?: string;
}

interface CaseStudies {
  title: string;
  description: string;
}

interface DataCatalogueEntry {
  id: string;
  displayProduct: string;
  description: string;
  shortDescription: string;
  metadata: Metadata;
  latestReleaseReportLink: string;
  productDescriptionLink: string;
  imageLight: any;
  imageDark: any;

  subtitle?: string;
  caseStudies?: CaseStudies[];
  dataDictionaries?: { [key: string]: string };
  apiProducts?: ApiProducts[];
  downloadLink?: string;
}

type DemoStatus = "private" | "public";

interface DemoStatusRequest {
  status: DemoStatus;
}

interface DemoAllowAccess {
  orgId: string;
  orgName: string;
  dateAdded: number;
}

interface DemoConfiguration {
  id: string;
  status: DemoStatus;
  dateAdded: number;
  dateModified: number;
  allow: DemoAllowAccess[];
}

interface MigrateMembersRequest {
  member_user_ids: string[];
  apps_names: string[];
  clips_ids: string[];
  custom_datasets_ids: string[];
  batch_jobs_ids: string[];
  new_organisation_id: string;
  current_organisation_id: string;
}

interface MigrateMembersResponse {
  members: Record<string, boolean>;
  apps: Record<string, boolean>;
  clips: Record<string, boolean>;
  custom_datasets: Record<string, boolean>;
}

interface CreditsInfo {
  // Remaining Balance
  totalCreditsBalance: number;
  subCreditsBalance: number;
  overageCreditsBalance: number;

  // Limits
  totalCreditsLimit: number;
  subCreditsLimit: number;
  overageDollarsLimit: number;
  overageCreditsLimit: number;
  overageDollarsMax: number;
  overageCreditsMax: number;

  // Consumed/Spent Balance
  totalCreditsSpent: number;
  subCreditsSpent: number;
  overageDollarsSpent: number;
  overageCreditsSpent: number;

  // Rates
  overageRateDollarsPerCredit: number;
  overageCreditsBlock: number;
  overageDollarsPerCreditsBlock: number;

  // Calculated flags for convenience
  isOverageEnabled: boolean;
  isInfiniteCredits: boolean;
  isNoTotalCredits: boolean;
  isNoSubCredits: boolean;
  isNoOverageCredits: boolean;
}

type PermissionDeniedReason =
  | "feature_disabled"
  | "upgrade_required"
  | "externally_managed"
  | "free_trial"
  | "role_deny";

type PermissionStatus = {
  allowed: boolean;
  reason?: PermissionDeniedReason;
};

type BaseCRUDActions = {
  create: PermissionStatus;
  read: PermissionStatus;
  update: PermissionStatus;
  delete: PermissionStatus;
};

type ClipActions = BaseCRUDActions & {
  oversized: PermissionStatus;
  payDollars: PermissionStatus;
};

type CreditActions = {
  unlimited: PermissionStatus;
};

type MemberActions = BaseCRUDActions & {
  changeOwner: PermissionStatus;
};

type ResourceActionsMap = {
  clip: ClipActions;
  credits: CreditActions;
  members: MemberActions;
  // Any not listed here get CRUDActions
};

type ResourcePermissions<
  T extends keyof ResourceActionsMap | undefined = undefined
> = T extends keyof ResourceActionsMap
  ? ResourceActionsMap[T]
  : BaseCRUDActions;

type PlanPermissions = {
  subscription: ResourcePermissions;
  payment_method: ResourcePermissions;
  overage: ResourcePermissions;
  apps: ResourcePermissions;
  batch: ResourcePermissions;
  premium_data: ResourcePermissions;
  groups: ResourcePermissions;
  roles: ResourcePermissions;
  permissions: ResourcePermissions;
  reports: ResourcePermissions;
  org_info: ResourcePermissions;

  members: ResourcePermissions<"members">;
  clip: ResourcePermissions<"clip">;
  credits: ResourcePermissions<"credits">;
};

type AdminOverwrites = {
  members?: ResourcePermissions;
};

type ResourceKey = keyof PlanPermissions;
type ActionKeysForResource<R extends ResourceKey> = keyof PlanPermissions[R];

type ConnectorType = "sftp" | "s3" | "snowflake";
type AuthType = "ssh" | "basic";
type SFTP_Type = "byo_sftp" | "geoscape_sftp";

interface SFTPBasicAuth {
  auth_type: "basic";
  username: string;
  password: string;
  password_submitted?: boolean;
}

interface SFTPSSHAuth {
  auth_type: "ssh";
  username: string;
  public_key: string;
}

type SFTPAuth = SFTPBasicAuth | SFTPSSHAuth;

interface BasePathConfigMixin {
  base_path?: string;
}

interface SFTPConnectorConfig extends BasePathConfigMixin {
  type: "byo_sftp" | "geoscape_sftp";
  host: string;
  port: number;
  auth: SFTPAuth;
  whitelisted_ips?: string[];
}

type S3Type = "s3_manual" | "s3_cloudformation";

interface ManualS3ConnectorConfig extends BasePathConfigMixin {
  type: "s3_manual";
  bucket_name: string;
  region?: string;
  // IAM Role-based authentication (cross-account)
  role_arn: string;
  external_id: string;
  account_id?: string;
  role_name?: string;
}

interface CloudFormationS3ConnectorConfig extends BasePathConfigMixin {
  type: "s3_cloudformation";
  bucket_name: string;
  region: string; // Required for CloudFormation template generation
  external_id: string; // Generated by Hub, used in CloudFormation template
  account_id: string; // AWS Account ID (12 digits) - required to construct role ARN
  // CloudFormation stack details (optional - use defaults if not provided)
  role_name?: string; // IAM role name created by CloudFormation (default: GeoscapeHubS3IntegrationRole)
  // AWS Console launch URL for easy deployment (points to static template in S3)
  console_launch_url?: string;
}

type S3ConnectorConfig = ManualS3ConnectorConfig | CloudFormationS3ConnectorConfig;

interface SnowflakeConnectorConfig {
  type: "snowflake";
  account: string;
  user: string;
  password: string;
  warehouse: string;
  database: string;
  schema: string;
}

type ConnectorConfig =
  | SFTPConnectorConfig
  | S3ConnectorConfig
  | SnowflakeConnectorConfig;

interface Connector {
  organisationId: string;
  connectorId: string;
  name: string;
  notify: boolean;
  type: ConnectorType;
  config: ConnectorConfig;
  lastConnected?: number;
}

// Pipelines

enum TriggerType {
  CRON = "cron",
  DATASET_RELEASE = "dataset_release",
  BATCH = "batch",
}

enum PipelineConnectorType {
  SFTP = "sftp",
  S3 = "s3",
}

interface CronTrigger {
  type: TriggerType.CRON;
  time: string;
  fileMatching: string;
}

interface DatasetReleaseTrigger {
  type: TriggerType.DATASET_RELEASE;
}

type Trigger = CronTrigger | DatasetReleaseTrigger;

interface RelativePathConfigMixin {
  relativePath?: string;
}

interface SFTPConnector extends RelativePathConfigMixin {
  type: PipelineConnectorType.SFTP;
  id: string;
}

interface S3Connector extends RelativePathConfigMixin {
  type: PipelineConnectorType.S3;
  id: string;
}

type PipelineConnector = SFTPConnector | S3Connector;

interface StandardDatasetMatching {
  type: "standard";
  product: string;
  state: string;
  CRS: string;
  format: string;
}

interface CustomDatasetMatching {
  type: "custom";
  name: string;
  region: string;
  format: string;
}

type DatasetMatching = StandardDatasetMatching | CustomDatasetMatching;

interface BatchAutomation {
  type: TriggerType.BATCH;
  batchId: string;
  fromConnector: PipelineConnector;
  toConnector: PipelineConnector;
}

interface DatasetPublishAutomation {
  type: TriggerType.DATASET_RELEASE;
  datasetsMatching: DatasetMatching[];
  toConnector: PipelineConnector;
}

type Automation = BatchAutomation | DatasetPublishAutomation;
type PipelineStatus = "active" | "inactive";
type PipelineLastRunStatus =
  | "RUNNING"
  | "SUCCEEDED"
  | "FAILED"
  | "TIMED_OUT"
  | "ABORTED"
  | "PENDING_REDRIVE"
  | "NONE";

interface PipelineLastRun {
  status: PipelineLastRunStatus;
  startDate?: number;
}

interface PipelineNextRun {
  nextRunDate?: string;
}

interface Pipeline {
  pipelineId: string;
  organisationId: string;
  trigger: Trigger;
  automation: Automation;
  name: string;
  description: string;
  notify: boolean;
  status: PipelineStatus;
  lastExecutionName?: string;
}

interface PipelineStatusBody {
  status: PipelineStatus;
}

interface ManualTriggerResponse {
  errors: string[];
  successes: string[];
}

interface ContractInfo {
  contract_start: string;
  contract_end: string;
  product_name: string;
  regions: string[];
}
interface UserLicences {
  dataset_products: ContractInfo[];
}

interface InvitationResponse {
  invitedMemberId: string;
  invitedEmail: string;
  userExisted: boolean;
  invitedName: string;
  status: string;
  role: Role;
}

// Buildings Sample types - using backend API format directly
type SampleDatum = "GDA2020" | "GDA94";

type SampleFormat = "GDB" | "GEOJSON" | "SHP" | "TAB" | "GPKG";

interface BuildingsSampleRequest {
  requestReason: string;
  industry: Industry;
  datum: SampleDatum;
  format: SampleFormat;
  canContact: boolean;
}

interface SSHKeyPairResponse {
  public_key: string;
  organisation_id: string;
  created: boolean;
}

export { PipelineConnectorType, Role, TriggerType };

export type {
  ActionKeysForResource,
  AdminAccountDetails,
  AdminOrganisationInfo,
  AdminOverwrites,
  AdminSearchAccount,
  AdminSearchOrganisation,
  AdminSubscription,
  AlertState,
  ApigeeDeveloperStatus,
  ApiKey,
  ApiProduct,
  AuthType,
  Automation,
  BatchAutomation,
  BillingInterval,
  BillingUsage,
  BuildingsSampleRequest,
  CaseStudies,
  CognitoStatus,
  Connector,
  ConnectorConfig,
  ConnectorType,
  ContractInfo,
  Coupon,
  CreateRelease,
  CreditItem,
  CreditsInfo,
  CreditUse,
  CronTrigger,
  CustomDataset,
  CustomDatasetDefinitions,
  CustomDatasetEdit,
  CustomDatasetFilterType,
  CustomDatasetList,
  CustomDatasetsPost,
  DataCatalogueEntry,
  Dataset,
  DatasetDefinitions,
  DatasetFiltersType,
  ProductDefinition,
  DatasetList,
  DatasetMatching,
  DatasetPublishAutomation,
  DatasetReleaseTrigger,
  DatasetStatus,
  DefinitionsMetadata,
  DemoAllowAccess,
  DemoConfiguration,
  DemoStatus,
  DeveloperStatus,
  Dictionary,
  FutureSubscription,
  HubPlan,
  Industry,
  InvitationResponse,
  ManualTriggerResponse,
  MemberAccountDetails,
  Metadata,
  MigrateMembersRequest,
  MigrateMembersResponse,
  ModifyApiKey,
  Organisation,
  OrganisationInvite,
  OrgMembership,
  Overage,
  PaymentMethod,
  PaymentMethodAdmin,
  PermissionDeniedReason,
  PermissionStatus,
  Pipeline,
  PipelineConnector,
  PipelineLastRun,
  PipelineLastRunStatus,
  PipelineNextRun,
  PipelineStatus,
  PipelineStatusBody,
  PlanInfo,
  PlanPermissions,
  PortalPlan,
  PortalSubscription,
  Product,
  ProductFeature,
  ProductIcon,
  PermissionDeniedReason as Reason,
  RejectedInvite,
  Release,
  ReleaseEventSummary,
  ReleaseStatus,
  ResourceKey,
  ResourcePermissions,
  S3Connector,
  S3ConnectorConfig,
  S3Type,
  ManualS3ConnectorConfig,
  CloudFormationS3ConnectorConfig,
  SampleDatum,
  SampleFormat,
  SFTP_Type,
  SFTPAuth,
  SFTPBasicAuth,
  SFTPConnector,
  SFTPConnectorConfig,
  SFTPSSHAuth,
  SnowflakeConnectorConfig,
  SSHKeyPairResponse,
  Status,
  Subscription,
  TrialType,
  Trigger,
  UpdateSubscription,
  UserLicences,
  UserMembership,
  UserStatus,
  WorkDescription,
};

export interface DeploymentInfo {
  project_name: string;
  branch_name: string;
  build_number: string;
  commit_hash?: string;
  is_latest_on_branch?: boolean;
  commits_behind_branch?: number;
  commits_behind_main?: number;
  commits_ahead_of_main?: number;
}

